/**
 * useThreeDSecure Hook
 *
 * Handles 3D Secure verification for card payments.
 * Only activates for countries where 3DS is legally required (PSD2/SCA).
 *
 * @example
 * const { threeDSecureReady, verifyCard } = useThreeDSecure({
 *   braintreeLoading: false,
 *   mode: 'mandatory',
 * });
 */
import { useState, useEffect, useCallback } from 'react';
import threeDSecure from 'braintree-web/three-d-secure';
import logger from '../utils/logger';

/**
 * 31 países donde 3D Secure es OBLIGATORIO por ley (PSD2/SCA)
 * - 27 países de la Unión Europea
 * - 3 países del Espacio Económico Europeo (EEA): Islandia, Liechtenstein, Noruega
 * - 1 país post-Brexit: Reino Unido (mantiene SCA)
 */
const MANDATORY_3DS_COUNTRIES = [
  // Unión Europea (27)
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA (3)
  'IS', 'LI', 'NO',
  // Post-Brexit (1)
  'GB',
];

/**
 * Get user-friendly error message for 3DS errors
 */
const get3DSUserMessage = (errorCode) => {
  const messages = {
    'THREEDS_LOOKUP_ERROR': 'Unable to verify your card. Please try again.',
    'THREEDS_CARDINAL_SDK_ERROR': 'Card verification service unavailable. Please try again.',
    'THREEDS_CARDINAL_SDK_SETUP_TIMEDOUT': 'Card verification timed out. Please try again.',
    'THREEDS_CARDINAL_SDK_RESPONSE_TIMEDOUT': 'Card verification timed out. Please try again.',
    'THREEDS_CARDINAL_SDK_BAD_CONFIG': 'Card verification configuration error.',
    'THREEDS_CARDINAL_SDK_BAD_JWT': 'Card verification session expired. Please refresh.',
    'THREEDS_AUTHENTICATION_ERROR': 'Card authentication failed. Please try a different card.',
    'THREEDS_HTTPS_REQUIRED': 'Secure connection required for card verification.',
    'THREEDS_NOT_ENABLED_FOR_V2': '3D Secure is not enabled for this merchant.',
    'THREEDS_MISSING_VERIFY_CARD_OPTION': 'Card verification misconfigured.',
  };
  return messages[errorCode] || 'Card verification failed. Please try again or use a different card.';
};

/**
 * Custom hook for 3D Secure integration via Braintree
 *
 * @param {Object} options
 * @param {boolean} options.braintreeLoading - Whether Braintree client is still loading
 * @param {string} options.mode - 3DS mode: 'disabled' | 'mandatory' | 'all_countries'
 * @returns {Object} 3DS state and methods
 */
export const useThreeDSecure = ({ braintreeLoading, mode = 'disabled' }) => {
  const [threeDSecureInstance, setThreeDSecureInstance] = useState(null);
  const [threeDSecureReady, setThreeDSecureReady] = useState(false);
  const [initError, setInitError] = useState(null);

  /**
   * Initialize 3D Secure instance after Braintree client is ready
   */
  useEffect(() => {
    // Wait if mode is not yet determined (config still loading)
    if (mode === null || mode === undefined) {
      logger.debug('3DS: Waiting for config...');
      return;
    }

    // Skip if disabled or already initialized
    if (mode === 'disabled') {
      logger.debug('3DS: Mode is disabled, skipping initialization');
      return;
    }

    if (braintreeLoading || !window.braintreeClient || threeDSecureInstance) {
      return;
    }

    const init3DS = async () => {
      try {
        logger.debug('3DS: Initializing with mode:', mode);

        const instance = await threeDSecure.create({
          version: 2,  // Use 3DS 2.0
          client: window.braintreeClient,
        });

        // Handle customer closing the 3DS modal
        instance.on('customer-canceled', () => {
          logger.debug('3DS: Customer closed verification modal');
        });

        // Handle authentication modal events (optional, for debugging)
        instance.on('authentication-modal-render', () => {
          logger.debug('3DS: Authentication modal rendered');
        });

        instance.on('authentication-modal-close', () => {
          logger.debug('3DS: Authentication modal closed');
        });

        setThreeDSecureInstance(instance);
        setThreeDSecureReady(true);
        logger.debug('3DS initialized successfully');

      } catch (err) {
        logger.error('3DS initialization error:', err);
        setInitError(err);
        // Don't block checkout - 3DS is optional enhancement
      }
    };

    init3DS();
  }, [braintreeLoading, mode, threeDSecureInstance]);

  /**
   * Determine if 3DS should be activated for a given country
   */
  const shouldActivate3DS = useCallback((billingCountryCode) => {
    if (mode === 'disabled') return false;
    if (mode === 'all_countries') return true;
    if (mode === 'mandatory') {
      return MANDATORY_3DS_COUNTRIES.includes(billingCountryCode?.toUpperCase());
    }
    return false;
  }, [mode]);

  /**
   * Verify a card with 3D Secure
   *
   * @param {Object} params
   * @param {string} params.nonce - Payment method nonce from tokenization
   * @param {string} params.bin - Card BIN (first 6-8 digits) from tokenization
   * @param {string} params.amount - Transaction amount as string (e.g., "99.99")
   * @param {string} params.email - Customer email
   * @param {Object} params.billingAddress - Billing address with Braintree field names
   * @returns {Promise<Object>} Verification result
   */
  const verifyCard = useCallback(async ({
    nonce,
    bin,
    amount,
    email,
    billingAddress
  }) => {
    const countryCode = billingAddress?.countryCodeAlpha2;

    // Check if 3DS should be activated for this country
    if (!shouldActivate3DS(countryCode)) {
      logger.debug(`3DS: Skipped for country ${countryCode} (mode: ${mode})`);
      return {
        skipped: true,
        nonce,
        reason: `3DS not required for ${countryCode}`
      };
    }

    // Check if 3DS is ready
    if (!threeDSecureInstance) {
      if (initError) {
        logger.warn('3DS: Instance not available due to init error, proceeding without 3DS');
        return { skipped: true, nonce, reason: '3DS initialization failed' };
      }
      throw new Error('3D Secure not initialized');
    }

    logger.debug(`3DS: Verifying card for ${countryCode}, amount: ${amount}`);

    return new Promise((resolve, reject) => {
      threeDSecureInstance.verifyCard({
        nonce,
        bin,
        amount,
        email,
        billingAddress: {
          givenName: billingAddress.givenName,
          surname: billingAddress.surname,
          streetAddress: billingAddress.streetAddress,
          extendedAddress: billingAddress.extendedAddress || '',
          locality: billingAddress.locality,
          region: billingAddress.region,
          postalCode: billingAddress.postalCode,
          countryCodeAlpha2: billingAddress.countryCodeAlpha2,
        },
        // Required callback for 3DS 2.0
        onLookupComplete: (data, next) => {
          logger.debug('3DS Lookup complete:', {
            requiresUserAuthentication: data.requiresUserAuthentication,
            threeDSecureVersion: data.paymentMethod?.threeDSecureInfo?.threeDSecureVersion,
          });
          // Continue to challenge if needed
          next();
        },
      }, (err, payload) => {
        if (err) {
          logger.error('3DS verification error:', err);
          reject({
            code: err.code,
            message: err.message,
            userMessage: get3DSUserMessage(err.code),
          });
          return;
        }

        const info = payload.threeDSecureInfo;
        logger.debug('3DS verification complete:', {
          status: info.status,
          liabilityShifted: info.liabilityShifted,
          liabilityShiftPossible: info.liabilityShiftPossible,
          version: info.threeDSecureVersion,
        });

        resolve({
          skipped: false,
          nonce: payload.nonce,
          threeDSecureInfo: {
            status: info.status,
            liabilityShifted: info.liabilityShifted,
            liabilityShiftPossible: info.liabilityShiftPossible,
            version: info.threeDSecureVersion,
            enrolled: info.enrolled,
          },
        });
      });
    });
  }, [threeDSecureInstance, shouldActivate3DS, mode, initError]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (threeDSecureInstance) {
        threeDSecureInstance.teardown().catch(() => {});
      }
    };
  }, [threeDSecureInstance]);

  return {
    threeDSecureReady,
    threeDSecureInstance,
    verifyCard,
    shouldActivate3DS,
    initError,
  };
};

export default useThreeDSecure;
